/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import Scroll from '@component/common/scroll';
import * as size from '@constant/size';
import { IS_IYAGIGO_MOBILE_VIEW, IS_IOS, IS_ANDROID } from '@config/environment';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import QuestionAndAnswer from '@component/iyagigo/hub/help/question-and-answer';
import Gap from '@component/common/gap';
import { useBeginStudy } from '@hook/iyagigo/useStudent';
import { IYAGIGO_IOS_STORE_URL, IYAGIGO_ANDROID_STORE_URL } from '@constant/iyagigo/variable';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING};
`;

const IndexContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin: 0 0 ${size.MARGIN_4} 0;
`;

const QuestionAndAnswerContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
`;

const FOOTER_HEIGHT = IS_IYAGIGO_MOBILE_VIEW ? '120px' : '100px';
const FOOTER_GAP = IS_IYAGIGO_MOBILE_VIEW ? size.MARGIN_1 : size.MARGIN_2;

// AnswerContainer도 max-width:500px
const FooterContainer = styled.div`
  position: absolute;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: flex-start;
  bottom: ${FOOTER_GAP};
  left: ${FOOTER_GAP};
  width: calc(100% - ${FOOTER_GAP} * 2);
  max-width: 500px;
  height: ${FOOTER_HEIGHT};
`;

const HELP_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2 },
  box: {
    alignX: 'left',
    padding: size.MARGIN_1,
    borderRadius: size.BORDER_RADIUS_2,
    backgroundColor: color.BROWN_4,
    width: IS_IYAGIGO_MOBILE_VIEW ? '100%' : 'fit-content',
  },
} as const;

const BEGIN_BUTTON_DESIGN = {
  text: { size: 'medium', color: color.WHITE_1 },
  box: {
    padding: size.MARGIN_1,
    borderRadius: size.BORDER_RADIUS_2,
    backgroundColor: color.BROWN_1,
    width: IS_IYAGIGO_MOBILE_VIEW ? '100%' : 'fit-content',
    event: { cursor: 'pointer' },
  },
} as const;

const INDEX_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  box: { alignX: 'left', margin: `0 0 5px 0` },
} as const;

const Help = () => {
  const handleBeginButtonClick = useBeginStudy();

  return (
    <Wrapper>
      <Scroll direction='y' height={`calc(100% - ${FOOTER_HEIGHT} - ${FOOTER_GAP} - 10px)`}>
        <IndexContainer>
          <TextBlock text={'시작하기'} design={INDEX_TEXT_DESIGN} />
          <QuestionAndAnswerContainer>
            <QuestionAndAnswer
              question={'이야기고는 어떤 서비스인가요?'}
              answer={`이야기고는 매일 10분, 일본어·영어를 간편하게 공부할 수 있는 학습 서비스입니다. 가벼운 마음으로 꾸준히 실력을 키울 수 있어요.`}
            />
            <QuestionAndAnswer
              // initialOpen={IS_IOS}
              initialOpen={false}
              question={'IOS 모바일 기기를 지원하나요?'}
              // answer={`<p  style="margin: 0 0 0 0" >이야기고는 IOS 모바일 기기를 지원합니다. \n홈 화면에 <strong>바로가기</strong>를 추가하여 더욱 간편하게 서비스를 이용할 수 있습니다.</p>
              // 1) Safari 앱에서 <u>https://iyagigo.com</u>을 열어, <u>공유버튼</u>을 클릭해주세요
              // <img src="/iyagigo/ios_1.png" width="250px" style="margin: 10px 0 10px 20px" />
              // 2) <u>홈 화면에 추가</u>를 클릭해주세요
              // <img src="/iyagigo/ios_2.png" width="250px" style="margin: 10px 0 10px 20px" />
              // 3) 바로가기 이름을 설정하고, <u>추가</u>를 클릭해주세요
              // <img src="/iyagigo/ios_3.jpg" width="250px" style="margin: 10px 0 10px 20px" />
              // 4)홈 화면에 바로가기가 추가됩니다.
              // <img src="/iyagigo/ios_4.png" width="250px" style="margin: 10px 0 10px 20px" />
              // `}
              answer={`이야기고는 iOS 모바일 기기를 지원합니다.\n<a href="${IYAGIGO_IOS_STORE_URL}" target="_blank"><strong><u>App Store에서 다운로드</u></strong></a>`}
            />
            <QuestionAndAnswer
              // initialOpen={IS_ANDROID}
              initialOpen={false}
              question={'안드로이드 모바일 기기를 지원하나요?'}
              // answer={`<p  style="margin: 0 0 0 0" >이야기고는 안드로이드 모바일 기기를 지원합니다. \n홈 화면에 <strong>바로가기</strong>를 추가하여 더욱 간편하게 서비스를 이용할 수 있습니다.</p>
              // 1) 브라우저에서 <u>https://iyagigo.com</u>을 열어, <u>더보기</u>를 클릭해주세요
              // <img src="/iyagigo/android_1.jpg" width="250px" style="margin: 10px 0 10px 20px" />
              // 2) <u>홈 화면에 추가</u>를 클릭해주세요
              // <img src="/iyagigo/android_2.jpg" width="250px" style="margin: 10px 0 10px 20px" />
              // 3) <u>바로가기 만들기</u>를 클릭해주세요
              // <img src="/iyagigo/android_3.jpg" width="250px" style="margin: 10px 0 10px 20px" />
              // 3) 바로가기 이름을 설정하고, <u>추가</u>를 클릭해주세요
              // <img src="/iyagigo/android_4.jpg" width="250px" style="margin: 10px 0 10px 20px" />
              // 4)홈 화면에 바로가기가 추가됩니다.
              // <img src="/iyagigo/android_5.jpg" width="250px" style="margin: 10px 0 0px 20px" />
              // `}
              // />
              answer={`이야기고는 안드로이드 모바일 기기를 지원합니다.\n<a href="${IYAGIGO_ANDROID_STORE_URL}" target="_blank"><strong><u>Play Store에서 다운로드</u></strong></a>`}
            />
          </QuestionAndAnswerContainer>
        </IndexContainer>
        <IndexContainer>
          <TextBlock text={'학습방법'} design={INDEX_TEXT_DESIGN} />
          <QuestionAndAnswerContainer>
            <QuestionAndAnswer
              question={'어떻게 어휘를 학습할 수 있나요?'}
              answer={`오늘의 어휘에서 매일 '빈칸채우기'를 통해 자연스럽게 익힐 수 있어요.`}
            />
            <QuestionAndAnswer
              question={'어떻게 문법을 학습할 수 있나요?'}
              answer={`오늘의 문법에서 매일 '문장만들기'를 통해 자연스럽게 익힐 수 있어요.`}
            />
            {/*            <QuestionAndAnswer*/}
            {/*              question={'어떻게 TOEIC을 학습할 수 있나요?'}*/}
            {/*              answer={`준비 중이거나 관심 있는 시험TOEIC 점수대(500·600·700·800·900점)를 선택하면, 매일 'LC', 'RC' 문제를 풀 수 있습니다.*/}
            {/*매일 10분, 꾸준히 풀다 보면 실력이 자연스럽게 올라갑니다.`}*/}
            {/*            />*/}
            {/*            <QuestionAndAnswer*/}
            {/*              question={'어떻게 JLPT를 학습할 수 있나요?'}*/}
            {/*              answer={`준비 중이거나 관심 있는 JLPT 단계(N1·2·3·4·5)를 선택하면, 매일 '문자·어휘', '문법', '독해', '청해' 문제를 풀 수 있습니다.*/}
            {/*매일 10분, 꾸준히 풀다 보면 실력이 자연스럽게 올라갑니다.`}*/}
            {/*            />*/}
            {/*<QuestionAndAnswer question={'일본어가 처음인데 어디서부터 시작해야할까요?'} answer={`초심자를 위한...`} />*/}
          </QuestionAndAnswerContainer>
        </IndexContainer>
        <IndexContainer>
          <TextBlock text={'계정'} design={INDEX_TEXT_DESIGN} />
          <QuestionAndAnswerContainer>
            <QuestionAndAnswer
              question={'회원과 비회원의 차이는 무엇인가요?'}
              answer={`비회원은 일부 학습자료만 볼 수 있고, 학습관리를 할 수 없어요.
회원가입하고 이야기고를 마음껏 이용해보세요.`}
            />
            <QuestionAndAnswer
              question={'회원가입은 어떻게 할수 있나요?'}
              answer={`네이버, 카카오, 구글, 애플 계정으로 간편 가입이 가능합니다.
이러한 계정이 없다면 이메일로 직접 가입할 수도 있어요. ✉️`}
            />
          </QuestionAndAnswerContainer>
        </IndexContainer>
      </Scroll>
      <FooterContainer>
        {/*<TextBlock text={'학습하러가기'} design={BEGIN_BUTTON_DESIGN} click={{ handler: handleBeginButtonClick }} />*/}
        <TextBlock
          text={
            IS_IYAGIGO_MOBILE_VIEW
              ? '다른 궁금한 점이 있는 경우, <br/>✉️ <strong><u>help@iyagigo.com</u></strong> 으로 메일을 보내주세요.'
              : '다른 궁금한 점이 있는 경우, ✉️ <strong><u>help@iyagigo.com</u></strong> 으로 메일을 보내주세요.'
          }
          design={HELP_TEXT_DESIGN}
        />
      </FooterContainer>
    </Wrapper>
  );
};

export default Help;
